import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, Badge, Btn, Input, Select, Field, Modal } from "@/components/ui-kit";
import { ROLE_LABELS, CLASSIFICATIONS, findMeta, fmtDate } from "@/lib/constants";
import { Users as UsersIcon, Plus, Pencil, Check, X, Mail } from "lucide-react";
import { InviteModal } from "@/components/users/InviteModal";
import { PendingInvites } from "@/components/users/PendingInvites";
import { LoginHistory } from "@/components/users/LoginHistory";

const ROLES = ["admin", "supervisor", "technician", "inventory_manager"];

export default function Users() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [invites, setInvites] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const load = () => {
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/users/invites").then((r) => setInvites(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const pending = users.filter((u) => u.status === "pending");

  const toggleActive = async (u) => {
    try { await api.put(`/users/${u.id}`, { is_active: !u.is_active }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="users-page">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold uppercase tracking-tight sm:text-3xl flex items-center gap-2"><UsersIcon size={24} /> Users & Roles</h1>
          <p className="text-sm text-muted-foreground">Provision accounts · assign RBAC roles & clearance.</p>
        </div>
        <div className="flex gap-2">
          <Btn onClick={() => setInviteOpen(true)} data-testid="invite-user-btn"><Mail size={16} /> Invite by Email</Btn>
          <Btn variant="outline" onClick={() => { setEdit(null); setOpen(true); }} data-testid="new-user-btn"><Plus size={16} /> Add User</Btn>
        </div>
      </div>
      {pending.length > 0 && <PendingRequests users={pending} onDone={load} />}
      <PendingInvites invites={invites} onDone={load} />
      <Card className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead><tr className="border-b border-border text-left font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            <th className="px-3 py-2">Name</th><th className="px-3 py-2">Email</th><th className="px-3 py-2">Role</th><th className="px-3 py-2">Clearance</th><th className="px-3 py-2">Status</th><th className="px-3 py-2 text-right">Actions</th>
          </tr></thead>
          <tbody>
            {users.filter((u) => u.status !== "pending").map((u) => {
              const clazz = findMeta(CLASSIFICATIONS, u.security_clearance);
              return (
                <tr key={u.id} className="border-b border-border/60 hover:bg-accent/40" data-testid={`user-row-${u.id}`}>
                  <td className="px-3 py-2 font-medium">{u.full_name}</td>
                  <td className="px-3 py-2 font-mono text-xs">{u.email}</td>
                  <td className="px-3 py-2"><Badge className="border-primary/40 text-primary">{ROLE_LABELS[u.role]}</Badge></td>
                  <td className="px-3 py-2"><Badge className={clazz.cls}>{clazz.label}</Badge></td>
                  <td className="px-3 py-2">
                    <button onClick={() => toggleActive(u)} data-testid={`toggle-${u.id}`} className={`rounded-full px-2 py-0.5 font-mono text-[10px] font-bold ${u.is_active ? "bg-emerald-500/15 text-emerald-500" : "bg-red-500/15 text-red-500"}`}>
                      {u.is_active ? "ACTIVE" : "DISABLED"}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => { setEdit(u); setOpen(true); }} data-testid={`edit-user-${u.id}`} className="rounded border border-border p-1 hover:bg-accent"><Pencil size={13} /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
      <LoginHistory />
      {inviteOpen && <InviteModal onClose={() => setInviteOpen(false)} onSent={() => { setInviteOpen(false); load(); }} />}
      {open && <UserForm user={edit} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function PendingRequests({ users, onDone }) {
  const [roles, setRoles] = useState({});
  const act = async (u, kind) => {
    try {
      if (kind === "approve") await api.post(`/users/${u.id}/approve`, { role: roles[u.id] || u.role });
      else await api.post(`/users/${u.id}/reject`);
      toast.success(kind === "approve" ? `${u.full_name} approved` : "Request rejected");
      onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <Card className="border-amber-500/40 p-3" data-testid="pending-requests">
      <div className="mb-2 font-mono text-[10px] uppercase tracking-widest text-amber-500">Sign-up requests awaiting approval ({users.length})</div>
      <div className="space-y-2">
        {users.map((u) => (
          <div key={u.id} className="flex flex-wrap items-center gap-3 rounded border border-border px-3 py-2 text-sm" data-testid={`pending-row-${u.id}`}>
            <div className="min-w-0 flex-1"><div className="font-medium">{u.full_name}</div><div className="font-mono text-xs text-muted-foreground">{u.email} · requested {ROLE_LABELS[u.role]} · {fmtDate(u.created_at)}</div></div>
            <Select className="w-44" value={roles[u.id] || u.role} onChange={(e) => setRoles({ ...roles, [u.id]: e.target.value })} data-testid={`pending-role-${u.id}`}>
              {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
            </Select>
            <Btn size="sm" onClick={() => act(u, "approve")} data-testid={`approve-${u.id}`}><Check size={14} /> Approve</Btn>
            <Btn size="sm" variant="outline" onClick={() => act(u, "reject")} data-testid={`reject-${u.id}`}><X size={14} /> Reject</Btn>
          </div>
        ))}
      </div>
    </Card>
  );
}

function UserForm({ user, onClose, onSaved }) {
  const [f, setF] = useState(user ? { full_name: user.full_name, email: user.email, role: user.role, security_clearance: user.security_clearance, password: "" } : { full_name: "", email: "", role: "technician", security_clearance: "unclassified", password: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!f.full_name || !f.email) { toast.error("Name and email required"); return; }
    try {
      if (user) {
        const payload = { full_name: f.full_name, role: f.role, security_clearance: f.security_clearance };
        if (f.password) payload.password = f.password;
        await api.put(`/users/${user.id}`, payload);
      } else {
        if (!f.password) { toast.error("Password required"); return; }
        await api.post("/users", f);
      }
      toast.success(user ? "User updated" : "User created"); onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <Modal open onClose={onClose} title={user ? "Edit User" : "Add User"}>
      <div className="space-y-3">
        <Field label="Full Name" required><Input data-testid="uf-name" value={f.full_name} onChange={set("full_name")} /></Field>
        <Field label="Email" required><Input data-testid="uf-email" type="email" value={f.email} onChange={set("email")} disabled={!!user} /></Field>
        <Field label="Role"><Select value={f.role} onChange={set("role")} data-testid="uf-role">{ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}</Select></Field>
        <Field label="Security Clearance"><Select value={f.security_clearance} onChange={set("security_clearance")}>{CLASSIFICATIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}</Select></Field>
        <Field label={user ? "Reset Password (leave blank to keep)" : "Password"} required={!user}><Input data-testid="uf-password" type="password" value={f.password} onChange={set("password")} /></Field>
        <div className="flex justify-end gap-2"><Btn variant="outline" onClick={onClose}>Cancel</Btn><Btn onClick={save} data-testid="user-submit-btn">{user ? "Save" : "Create"}</Btn></div>
      </div>
    </Modal>
  );
}
